/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "admin/components/MDBox";
import MDInput from "admin/components/MDInput";
import MDButton from "admin/components/MDButton";
import MDTypography from "admin/components/MDTypography";

// List of countries and regions
const COUNTRIES_LIST = [
  "Syria",
  "Lebanon",
  "Palestine",
  "Jordan",
  "Iraq",
  "Yemen",
  "Egypt",
  "Saudi Arabia",
  "UAE",
  "Kuwait",
  "Bahrain",
  "Qatar",
  "Oman",
  "Turkey",
  "Iran",
  "Afghanistan",
  "Pakistan",
  "Libya",
  "Tunisia",
  "Algeria",
  "Morocco",
  "Sudan",
  "Somalia",
];

function UserModal({ open, onClose, user, onSave }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    companyLocation: "",
    email: "",
    phoneNo: "",
    password: "",
    role: "user",
    status: "active",
    geographicalCoverage: [],
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      // Edit mode - populate form with user data
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        company: user.company || "",
        companyLocation: user.companyLocation || "",
        email: user.email || "",
        phoneNo: user.phoneNo || "",
        password: "", // Don't populate password on edit
        role: user.role || "user",
        status: user.status || "active",
        geographicalCoverage: user.geographicalCoverage || [],
      });
    } else {
      // Add mode - reset form
      setFormData({
        firstName: "",
        lastName: "",
        company: "",
        companyLocation: "",
        email: "",
        phoneNo: "",
        password: "",
        role: "user",
        status: "active",
        geographicalCoverage: [],
      });
    }
  }, [user, open]);

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleRemoveCountry = (countryName) => {
    setFormData({
      ...formData,
      geographicalCoverage: formData.geographicalCoverage.filter((c) => c.country !== countryName),
    });
  };

  const handleToggleCenterMap = (countryName) => {
    setFormData({
      ...formData,
      geographicalCoverage: formData.geographicalCoverage.map((c) => ({
        ...c,
        isCenter: c.country === countryName ? !c.isCenter : false,
      })),
    });
  };

  const handleSave = () => {
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert("Please fill in all required fields (First Name, Last Name, Email)");
      return;
    }

    // Password validation for new users
    if (!user && !formData.password) {
      alert("Password is required for new users");
      return;
    }

    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDTypography variant="h5">{user ? "Edit user" : "Add/edit user"}</MDTypography>
      </DialogTitle>
      <DialogContent>
        <MDBox component="form" role="form" mt={2}>
          <Grid container spacing={2}>
            {/* First Name and Last Name */}
            <Grid item xs={12} md={6}>
              <MDInput
                type="text"
                label="First Name"
                fullWidth
                value={formData.firstName}
                onChange={handleInputChange("firstName")}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                type="text"
                label="Last Name"
                fullWidth
                value={formData.lastName}
                onChange={handleInputChange("lastName")}
                required
              />
            </Grid>

            {/* Company and Company Location */}
            <Grid item xs={12} md={6}>
              <MDInput
                type="text"
                label="Company"
                fullWidth
                value={formData.company}
                onChange={handleInputChange("company")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                type="text"
                label="Company location"
                fullWidth
                value={formData.companyLocation}
                onChange={handleInputChange("companyLocation")}
              />
            </Grid>

            {/* Email and Phone */}
            <Grid item xs={12} md={6}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={formData.email}
                onChange={handleInputChange("email")}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                type="text"
                label="Phone number"
                fullWidth
                value={formData.phoneNo}
                onChange={handleInputChange("phoneNo")}
              />
            </Grid>

            {/* Password with View Toggle */}
            <Grid item xs={12}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label={user ? "Password (leave empty to keep current)" : "Password"}
                fullWidth
                value={formData.password}
                onChange={handleInputChange("password")}
                required={!user}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        <Icon>{showPassword ? "visibility_off" : "visibility"}</Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Role and Status */}
            <Grid item xs={12} md={6}>
              <MDInput
                select
                label="Role"
                fullWidth
                value={formData.role}
                onChange={handleInputChange("role")}
                InputLabelProps={{ shrink: true }}
                InputProps={{ style: { height: "50px" } }}
                SelectProps={{ style: { height: "50px" } }}
                sx={{ height: "50px" }}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </MDInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={handleInputChange("status")}
                InputLabelProps={{ shrink: true }}
                InputProps={{ style: { height: "50px" } }}
                SelectProps={{ style: { height: "50px" } }}
                sx={{ height: "50px" }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </MDInput>
            </Grid>

            {/* Geographical Coverage Section - Only for Users */}
            {formData.role === "user" && (
              <Grid item xs={12}>
                <MDBox mt={2}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Geographical coverage (only for users, not admin: admin have access to everything)
                  </MDTypography>

                  {/* Countries Display */}
                  <MDBox display="flex" flexWrap="wrap" gap={2} mb={2}>
                    {formData.geographicalCoverage.map((country) => (
                      <MDBox key={country.country} display="flex" flexDirection="column" alignItems="flex-start">
                        <Chip
                          label={country.country}
                          onDelete={() => handleRemoveCountry(country.country)}
                          sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={country.isCenter}
                              onChange={() => handleToggleCenterMap(country.country)}
                              sx={{ padding: "4px" }}
                            />
                          }
                          label={
                            <MDTypography variant="caption" color="text">
                              Center map in this location
                            </MDTypography>
                          }
                        />
                      </MDBox>
                    ))}
                  </MDBox>

                  {/* Add Country - Autocomplete Dropdown */}
                  <MDBox mb={2}>
                    <Autocomplete
                      options={COUNTRIES_LIST.filter(
                        (country) => !formData.geographicalCoverage.find((c) => c.country === country)
                      )}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFormData({
                            ...formData,
                            geographicalCoverage: [
                              ...formData.geographicalCoverage,
                              {
                                country: newValue,
                                isCenter: false,
                              },
                            ],
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Add a country"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  </MDBox>
                </MDBox>
              </Grid>
            )}
          </Grid>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} variant="outlined" color="secondary">
          Cancel
        </MDButton>
        <MDButton onClick={handleSave} variant="gradient" color="info">
          SAVE
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

UserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

UserModal.defaultProps = {
  user: null,
};

export default UserModal;
